import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import pluralize from 'pluralize';
import { ToolbarOptions } from '../StudyListToolbar';
import { StudyListSidebar } from './StudyListSidebar';
import { CriteriaSidebarEmptyState } from './CriteriaSidebarEmptyState';
import { SortAndFilterSidebarContent } from './SortAndFilterSidebarContent';
import type { FilterSortDisplayForm } from './useFilterSortDisplayForm';
import { TagsPanel, TagsPanelProps } from './TagsPanel';
import {
  StudyListDisplayOptions,
  HighlightsPanel,
} from 'pages/EligibilityCriteria/components/HighlightsModal/HighlightsPanel';
import {
  EditableCriteria,
  EditableCriteriaHandle,
} from 'components/shared/EditableCriteria';
import { useStudyListLayout } from 'pages/StudyList/StudyListLayoutContext';

export type SidebarName = 'criteria' | 'highlights' | 'sortAndFilter' | 'tags';

type Props = {
  currentSidebar: SidebarName | null;
  criteria: {
    editableCriteriaRef: React.RefObject<EditableCriteriaHandle>;
    disclosureId: string;
    toggleRef: React.RefObject<HTMLButtonElement>;
    showEmptyState: boolean;
    openWindow: () => void;
    close: () => void;
    onCloseConfirmed: () => void;
  };
  highlights: {
    disclosureId: string;
    toggleRef: React.RefObject<HTMLButtonElement>;
    displayOptions: StudyListDisplayOptions;
  };
  sortAndFilter: {
    disclosureId: string;
    toggleRef: React.RefObject<HTMLButtonElement>;
    form: UseFormReturn<FilterSortDisplayForm>;
    sortOptions: Record<string, string>;
    tags: string[];
    isAwaitingOtherReviewer: boolean;
    reviewStage: ToolbarOptions['reviewStage'];
    isResetting: boolean;
    onApply: (values: FilterSortDisplayForm) => Promise<void>;
    onReset: () => Promise<void>;
  };
  tags: {
    disclosureId: string;
    toggleRef: React.RefObject<HTMLButtonElement>;
    panelProps: Omit<TagsPanelProps, 'layout'>;
  };
  closeSidebar: (name: Exclude<SidebarName, 'criteria'>) => void;
  focusFallbackRef: React.RefObject<HTMLElement>;
};

export const SidebarPanel = ({
  currentSidebar,
  criteria,
  highlights,
  sortAndFilter,
  tags,
  closeSidebar,
  focusFallbackRef,
}: Props) => {
  const { sidebarPanelRoot: contextPanelRoot } = useStudyListLayout();
  // Backbone pages render the panel in haml, so fall back to finding it by id
  const sidebarPanelRoot =
    contextPanelRoot ?? document.getElementById('studyListSidebarPanel');
  if (!sidebarPanelRoot) {
    return null;
  }

  const criteriaHeadingId = `${criteria.disclosureId}-heading`;
  const highlightsHeadingId = `${highlights.disclosureId}-heading`;
  const sortAndFilterHeadingId = `${sortAndFilter.disclosureId}-heading`;
  const tagsHeadingId = `${tags.disclosureId}-heading`;
  const tagsStudyCount = tags.panelProps.studiesSelectedCount;
  const tagsHeading = `Add ${pluralize('tag', tagsStudyCount)} to ${pluralize(
    'study',
    tagsStudyCount,
    true
  )}`;

  // Collapsing unmounts the focused element, so place focus deliberately
  // once the exit completes. Focus that has already moved on — to the trigger
  // of the sidebar taking over, say — is left where it is.
  const focusToggleAfterClose = (
    toggleRef: React.RefObject<HTMLButtonElement>
  ) => () => {
    const focused = document.activeElement;
    const hasFocusMovedElsewhere =
      focused &&
      focused !== document.body &&
      !sidebarPanelRoot?.contains(focused);
    if (hasFocusMovedElsewhere) return;

    // The trigger can unmount while open (applying tags clears the selection,
    // a vote removes the study). The sticky toolbar is a target that won't scroll.
    if (!toggleRef.current?.isConnected) {
      focusFallbackRef.current
        ?.querySelector<HTMLElement>('button, input')
        ?.focus();
      return;
    }

    toggleRef.current?.focus();
  };

  return (
    <>
      <EditableCriteria
        ref={criteria.editableCriteriaRef}
        headingId={criteriaHeadingId}
        pollForUpdates
        onOpenWindow={criteria.openWindow}
        onCloseConfirmed={criteria.onCloseConfirmed}
        emptyState={
          criteria.showEmptyState ? <CriteriaSidebarEmptyState /> : undefined
        }
      >
        {({ title, panel, isSaving }) => (
          <StudyListSidebar
            closeButtonLabel="Close eligibility criteria"
            disclosureId={criteria.disclosureId}
            headingId={criteriaHeadingId}
            isOpen={currentSidebar === 'criteria'}
            onClose={criteria.close}
            onClosed={focusToggleAfterClose(criteria.toggleRef)}
            panelRoot={sidebarPanelRoot}
            title={title}
            isCloseDisabled={isSaving}
          >
            {panel}
          </StudyListSidebar>
        )}
      </EditableCriteria>
      <StudyListSidebar
        closeButtonLabel="Close highlights"
        disclosureId={highlights.disclosureId}
        headingId={highlightsHeadingId}
        isOpen={currentSidebar === 'highlights'}
        onClose={() => closeSidebar('highlights')}
        onClosed={focusToggleAfterClose(highlights.toggleRef)}
        panelRoot={sidebarPanelRoot}
        title="Highlights"
        hasHeaderDivider
      >
        <HighlightsPanel
          layout="sidebar"
          studyListDisplayOptions={highlights.displayOptions}
          pollForUpdates
        />
      </StudyListSidebar>
      <StudyListSidebar
        closeButtonLabel="Close sort and filter"
        disclosureId={sortAndFilter.disclosureId}
        headingId={sortAndFilterHeadingId}
        isOpen={currentSidebar === 'sortAndFilter'}
        onClose={() => closeSidebar('sortAndFilter')}
        onClosed={focusToggleAfterClose(sortAndFilter.toggleRef)}
        panelRoot={sidebarPanelRoot}
        title="Sort & Filter"
        hasHeaderDivider
      >
        <SortAndFilterSidebarContent
          form={sortAndFilter.form}
          sortOptions={sortAndFilter.sortOptions}
          tags={sortAndFilter.tags}
          isAwaitingOtherReviewer={sortAndFilter.isAwaitingOtherReviewer}
          reviewStage={sortAndFilter.reviewStage}
          isResetting={sortAndFilter.isResetting}
          onApply={sortAndFilter.onApply}
          onReset={sortAndFilter.onReset}
        />
      </StudyListSidebar>
      <StudyListSidebar
        title={tagsHeading}
        headingId={tagsHeadingId}
        disclosureId={tags.disclosureId}
        closeButtonLabel="Close tags"
        isOpen={currentSidebar === 'tags'}
        onClose={() => closeSidebar('tags')}
        onClosed={focusToggleAfterClose(tags.toggleRef)}
        panelRoot={sidebarPanelRoot}
        hasHeaderDivider
      >
        <TagsPanel layout="sidebar" {...tags.panelProps} />
      </StudyListSidebar>
    </>
  );
};
